import {
  ClockIcon,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  TIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCatalogueSelectProps } from '@/components/CatalogueListbox/use-catalogue-select-props';
import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputDate } from '@/components/FormFields/InputDate';
import { InputNumber } from '@/components/FormFields/InputNumber';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { StateForm } from '@/components/FormFields/StateForm';
import { VISIBILITY_OPTIONS } from '@/constants/visibility';
import { TBroadcastScheduleProgramBodySegmentToolkitPeopleForm } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitPeople/types';
import { DrawnVideo } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/DrawnVideo/DrawnVideo';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { FeaturedPeopleDrawerForm } from '@/features/featured-people/components/FeaturedPeopleDrawerForm';
import { TFeaturedPeopleForm, TFeaturedPeopleType } from '@/features/featured-people/types';
import { CataloguePicker } from '@/hooks/use-catalogue-picker';
import { ReturnDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { I18nextTranslationKeys } from '@/i18n/i18next';

type TAdditionalButton = {
  label: string;
  onClick: () => void;
  icon?: TIcon;
  disabled?: boolean;
};

type TPeopleFormProps = {
  form: UseFormReturn<TBroadcastScheduleProgramBodySegmentToolkitPeopleForm>;
  peoplePicker: CataloguePicker<TFeaturedPeopleType>;
  selectedPeople: TFeaturedPeopleType | null | undefined;
  isCreatingPeople: boolean;
  peopleDrawer: ReturnDisclosureWithData<unknown>;
  handleClickAdd: () => void;
  handleClickExit: () => void;
  handleSubmit: (data: TBroadcastScheduleProgramBodySegmentToolkitPeopleForm) => void;
  handleSubmitPeople: (data: TFeaturedPeopleForm) => Promise<TFeaturedPeopleType>;
  renderAdditionalButtons: () => TAdditionalButton[];
  onClickTooltipDate: (name: 'start_datetime' | 'end_datetime') => void;
};

export function PeopleForm({
  form,
  peoplePicker,
  selectedPeople,
  isCreatingPeople,
  peopleDrawer,
  handleClickAdd,
  handleClickExit,
  handleSubmit,
  handleSubmitPeople,
  renderAdditionalButtons,
  onClickTooltipDate,
}: TPeopleFormProps) {
  const { personaClipData, personaData } = useBroadcastScheduleProgram();
  const { t } = useTranslation();

  const presenceData = personaClipData || personaData;

  const peopleSelectProps = useCatalogueSelectProps(peoplePicker);
  const isOptionsLoading = peoplePicker.isValueResolving;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClickExit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClickExit]);

  return (
    <>
      <form>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between border-b-[1px] border-accent-disabled-10">
            <Typography
              fontSize={FontSizeEnum.Xl}
              color={FontColorEnum.Dark10}
              weight={FontWeightEnum.Medium}
            >
              {`${presenceData ? t('Editar persona de interés') : t('Asignar persona de interés')}`}
            </Typography>

            <IconButton
              size={IconButtonSizeEnum.LG}
              color={IconButtonColorEnum.NONE_DEFAULT}
              onClick={handleClickExit}
            >
              <CloseIcon size={IconSizeEnum.LG} />
            </IconButton>
          </div>
          <div className="flex flex-1 gap-1">
            <div className="flex w-1/2 items-center justify-center p-4">
              {selectedPeople?.parameters?.main_logo_presigned_url && (
                <img
                  alt="persona"
                  className="h-full max-h-[200px] w-full max-w-[200px] border-0 object-contain"
                  src={selectedPeople.parameters.main_logo_presigned_url}
                />
              )}
            </div>
            <div className="flex w-1/2 flex-col gap-2">
              <DrawnVideo form={form} name="parameters.bbox" onClick={handleClickAdd} />
            </div>
          </div>

          <RowContainer>
            <SelectForm
              form={form}
              name="people_id"
              required
              label={t('Persona')}
              {...peopleSelectProps}
              isLoading={isOptionsLoading}
              additionalButtons={renderAdditionalButtons()}
            />
            <SelectForm
              form={form}
              name="parameters.visibility"
              required
              label={t('Visibilidad')}
              options={VISIBILITY_OPTIONS}
            />
          </RowContainer>
          <RowContainer>
            <InputDate
              form={form}
              name="start_datetime"
              label={t('Inicio')}
              seconds
              help
              tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
              iconTooltip={ClockIcon}
              onClickTooltip={() => onClickTooltipDate('start_datetime')}
            />
            <InputDate
              form={form}
              name="end_datetime"
              label={t('Fin')}
              seconds
              help
              tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
              iconTooltip={ClockIcon}
              onClickTooltip={() => onClickTooltipDate('end_datetime')}
            />
          </RowContainer>
          {/*       <RowContainer>
        <InputNumber form={form} name="value" required label={t('Valoración')} />
        <InputNumber form={form} name="audience" required label={t('Audiencia')} />
        </RowContainer> */}
          <RowContainer>
            <InputNumber
              form={form}
              name="entity_score"
              required
              label={t('Fiabilidad')}
              min={0}
              max={100}
            />
          </RowContainer>
          <StateForm
            className="!justify-start"
            form={form}
            name="review_state"
            activedName={t('Marcar como revisado')}
            desactivedName={t('No revisado')}
          />

          <FooterFormActions
            form={form}
            handleCancel={handleClickExit}
            handleSubmit={handleSubmit}
            isLoading={isOptionsLoading}
          />
        </div>
      </form>

      <FeaturedPeopleDrawerForm
        isOpen={peopleDrawer.isOpen}
        isMutating={isCreatingPeople}
        onClose={peopleDrawer.close}
        onSubmit={handleSubmitPeople}
      />
    </>
  );
}
